import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useSiteName } from '../../contexts/SiteSettingsContext';
import { Truck, FileDown, Package, MapPin, Mail, RotateCcw, Printer, FileSpreadsheet, BanknoteArrowUp, Undo2, CheckCircle2, Ban, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['pending', 'paid', 'shipped', 'delivered'];
const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', completed: 'bg-green-100 text-green-800', refunded: 'bg-red-100 text-red-800', cancelled: 'bg-gray-200 text-gray-700', rto: 'bg-orange-100 text-orange-800' };

const AdminOrders = () => {
  const siteName = useSiteName();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shippingDialog, setShippingDialog] = useState(null);
  const [postcode, setPostcode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pkg, setPkg] = useState({ length: '', breadth: '', height: '', weight: '' });
  const [pkgSaved, setPkgSaved] = useState(false);
  const [savingPkg, setSavingPkg] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [codStatusFilter, setCodStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [couriers, setCouriers] = useState([]);
  const [overrideCourierId, setOverrideCourierId] = useState('');
  const [resolvedCourier, setResolvedCourier] = useState(null);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupError, setPickupError] = useState('');

  useEffect(() => { fetchOrders(); }, []);

  const openShippingDialog = async (order) => {
    setShippingDialog(order.order_id);
    setPostcode('');
    const addr = order.shipping_address || {};
    setFirstName(addr.first_name || '');
    setLastName(addr.last_name || '');
    const existingPkg = order.packaging || {};
    const hasAll = ['length','breadth','height','weight'].every(k => Number(existingPkg[k]) > 0);
    setPkg({
      length: existingPkg.length ? String(existingPkg.length) : '',
      breadth: existingPkg.breadth ? String(existingPkg.breadth) : '',
      height: existingPkg.height ? String(existingPkg.height) : '',
      weight: existingPkg.weight ? String(existingPkg.weight) : '',
    });
    setPkgSaved(hasAll);
    setOverrideCourierId(order.courier_override_id ? String(order.courier_override_id) : '');
    setResolvedCourier(null);
    setPickupError('');
    // load couriers + resolved courier + pickup locations in parallel
    try {
      const [cRes, rRes, pRes] = await Promise.all([
        couriers.length ? Promise.resolve({ data: { couriers } }) : axios.get(`${API}/api/admin/couriers`, { withCredentials: true }),
        axios.get(`${API}/api/admin/orders/${order.order_id}/resolved-courier`, { withCredentials: true }),
        axios.get(`${API}/api/admin/shiprocket/pickup-locations`, { withCredentials: true }).catch((err) => ({ _err: err.response?.data?.detail || 'Failed to load pickup locations' })),
      ]);
      if (cRes.data?.couriers) setCouriers(cRes.data.couriers);
      setResolvedCourier(rRes.data?.resolved || null);
      if (pRes?._err) {
        setPickupLocations([]);
        setPickupLocation('');
        setPickupError(pRes._err);
      } else {
        const locs = pRes.data?.locations || [];
        setPickupLocations(locs);
        // Preselect: value already saved on order > default from Shiprocket > first available
        const saved = order.pickup_location || '';
        const preferred = (saved && locs.some(l => l.pickup_location === saved))
          ? saved
          : (pRes.data?.default || (locs[0]?.pickup_location || ''));
        setPickupLocation(preferred);
      }
    } catch { /* soft-fail, shipment still possible */ }
  };

  const saveCourierOverride = async () => {
    if (!shippingDialog) return;
    try {
      const courier = couriers.find((c) => String(c.courier_company_id) === String(overrideCourierId));
      await axios.put(`${API}/api/admin/orders/${shippingDialog}/courier`, {
        courier_id: overrideCourierId ? Number(overrideCourierId) : null,
        courier_name: courier?.courier_name || '',
      }, { withCredentials: true });
      toast.success(overrideCourierId ? 'Courier override saved' : 'Courier override cleared');
      // refresh resolved preview
      const { data } = await axios.get(`${API}/api/admin/orders/${shippingDialog}/resolved-courier`, { withCredentials: true });
      setResolvedCourier(data?.resolved || null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save override');
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/api/orders`, { withCredentials: true });
      setOrders(data);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/api/orders/${orderId}/status`, { status }, { withCredentials: true });
      toast.success(`Status updated to ${status}`);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const savePackaging = async () => {
    if (!shippingDialog) return;
    const l = Number(pkg.length), b = Number(pkg.breadth), h = Number(pkg.height), w = Number(pkg.weight);
    if (!(l > 0) || !(b > 0) || !(h > 0) || !(w > 0)) {
      toast.error('All four packaging fields must be greater than zero');
      return;
    }
    setSavingPkg(true);
    try {
      await axios.put(`${API}/api/admin/orders/${shippingDialog}/packaging`, { length: l, breadth: b, height: h, weight: w }, { withCredentials: true });
      toast.success('Packaging details saved');
      setPkgSaved(true);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save packaging');
    } finally {
      setSavingPkg(false);
    }
  };

  const createShipment = async (orderId) => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First Name and Last Name are required before shipping');
      return;
    }
    if (!pickupLocation) {
      toast.error('Select a Pickup Location before shipping');
      return;
    }
    try {
      const body = { order_id: orderId, delivery_postcode: postcode || '', first_name: firstName.trim(), last_name: lastName.trim(), pickup_location: pickupLocation };
      if (overrideCourierId) body.courier_id = Number(overrideCourierId);
      const { data } = await axios.post(`${API}/api/shipping/create`, body, { withCredentials: true });
      toast.success(`Shipment created: ${data.tracking_id}${data.courier_name ? ` via ${data.courier_name}` : ''}`);
      setShippingDialog(null);
      setPostcode('');
      setFirstName('');
      setLastName('');
      setPkg({ length: '', breadth: '', height: '', weight: '' });
      setPkgSaved(false);
      setOverrideCourierId('');
      setPickupLocation('');
      setPickupLocations([]);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create shipment'); }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/api/invoice/${orderId}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download invoice'); }
  };

  const emailInvoice = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/email-invoice`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to email invoice'); }
  };

  const refundOrder = async (orderId, amount) => {
    if (!window.confirm(`Refund ₹${amount.toFixed(2)} for order ${orderId}? This cannot be undone.`)) return;
    try {
      const { data } = await axios.post(`${API}/api/refund/${orderId}`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Refund failed'); }
  };

  const collectCOD = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/cod/collect`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to mark collected'); }
  };

  const undoCollectCOD = async (orderId) => {
    if (!window.confirm('Undo COD collection for this order?')) return;
    try {
      const { data } = await axios.post(`${API}/api/orders/${orderId}/cod/undo-collect`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to undo collection'); }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm(`Cancel order ${orderId}? Stock will be restored and the customer will be emailed. This cannot be undone.`)) return;
    try {
      const { data } = await axios.post(`${API}/api/admin/orders/${orderId}/cancel`, {}, { withCredentials: true });
      toast.success(data.message || 'Order cancelled');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel order'); }
  };

  const deleteOrder = async (orderId) => {
    const typed = window.prompt(`PERMANENTLY delete order ${orderId}? This is irreversible — the order will be removed from the live list (a backup copy is kept in deleted_orders).\n\nType DELETE (uppercase) to confirm:`, '');
    if (typed == null) return;
    if (typed !== 'DELETE') { toast.error('Confirmation text did not match. Order was NOT deleted.'); return; }
    try {
      const { data } = await axios.delete(`${API}/api/admin/orders/${orderId}`, {
        withCredentials: true,
        data: { confirm: 'DELETE' },
      });
      toast.success(data.message || 'Order deleted');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete order'); }
  };

  const filteredOrders = orders.filter(order => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || order.order_id.toLowerCase().includes(q) || (order.shipping_address?.name || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const isCod = (order.payment_method || '').toLowerCase() === 'cod';
    const codStatus = order.cod_status || (isCod ? 'pending' : '');
    const matchesCod = codStatusFilter === 'all'
      || (codStatusFilter === 'cod_pending' && isCod && codStatus !== 'collected')
      || (codStatusFilter === 'cod_collected' && isCod && codStatus === 'collected')
      || (codStatusFilter === 'non_cod' && !isCod);
    const orderDate = order.created_at ? new Date(order.created_at) : null;
    const fromOk = !dateFrom || (orderDate && orderDate >= new Date(`${dateFrom}T00:00:00`));
    const toOk = !dateTo || (orderDate && orderDate <= new Date(`${dateTo}T23:59:59.999`));
    return matchesSearch && matchesStatus && matchesCod && fromOk && toOk;
  });

  const setToday = () => {
    const today = new Date().toISOString().slice(0, 10);
    setDateFrom(today);
    setDateTo(today);
  };

  const clearDates = () => { setDateFrom(''); setDateTo(''); };

  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const exportCSV = () => {
    if (!filteredOrders.length) { toast.error('No orders to export'); return; }
    const headers = ['Order ID', 'Order Date', 'Channel', 'Payment Method', 'Customer First Name', 'Customer Last Name', 'Customer Mobile Number', 'Shipping Address Line 1', 'Shipping Address Line 2', 'Shipping Address Country', 'Shipping Address State', 'Shipping Address City', 'Shipping Address Postcode', 'Master SKU', 'Product Name', 'Product Quantity', 'Selling Price', 'Partial COD', 'Length (cm)', 'Breadth (cm)', 'Height (cm)', 'Weight Of Shipment (kg)'];
    const formatOrderDate = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };
    const rows = [];
    filteredOrders.forEach(o => {
      const addr = o.shipping_address || {};
      const orderCols = [
        o.order_id,
        formatOrderDate(o.created_at),
        '',
        (o.payment_method || '').toLowerCase() === 'cod' ? 'COD' : 'Prepaid',
        addr.first_name || o.guest_first_name || '',
        addr.last_name || o.guest_last_name || '',
        addr.phone || o.guest_phone || '',
        addr.line1 || '',
        addr.line2 || '',
        'India',
        addr.state || '',
        addr.city || '',
        addr.pincode || '',
      ];
      (o.items || []).forEach(item => {
        rows.push([
          ...orderCols,
          item.product_id || '',
          item.name || '',
          item.quantity ?? '',
          item.price ?? '',
          'No',
          o.packaging?.length ?? '',
          o.packaging?.breadth ?? '',
          o.packaging?.height ?? '',
          o.packaging?.weight ?? '',
        ]);
      });
    });
    const csv = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} order items`);
  };

  const printOrdersList = (list, { title = 'Orders', subtitle = '', showCodBreakdown = false, signatureBlock = false } = {}) => {
    if (!list.length) { toast.error(`No ${title.toLowerCase()} to print`); return; }
    const w = window.open('', '_blank', 'width=1024,height=800');
    if (!w) { toast.error('Popup blocked. Please allow popups to print.'); return; }
    const rowsHtml = list.map(o => {
      const addr = o.shipping_address || {};
      const items = (o.items || []).map(i => `${i.name} x${i.quantity}`).join('<br/>');
      const isCod = (o.payment_method || '').toLowerCase() === 'cod';
      const codLabel = isCod ? `COD · ${o.cod_status === 'collected' ? 'Collected' : 'Pending'}` : 'Razorpay';
      return `<tr>
        <td>${o.order_id}</td>
        <td>${new Date(o.created_at).toLocaleDateString('en-IN')}</td>
        <td>${addr.name || o.guest_name || ''}<br/><span class="muted">${addr.phone || o.guest_phone || ''}</span></td>
        <td>${addr.line1 || ''} ${addr.city || ''} - ${addr.pincode || ''}</td>
        <td>${items}</td>
        <td>${codLabel}</td>
        <td>${o.status}</td>
        <td class="right">\u20B9${(o.total_amount || 0).toFixed(2)}</td>
      </tr>`;
    }).join('');
    const totalSum = list.reduce((s, o) => s + (o.total_amount || 0), 0);
    const codCollectedSum = list.filter(o => (o.payment_method || '').toLowerCase() === 'cod' && o.cod_status === 'collected').reduce((s, o) => s + (o.total_amount || 0), 0);
    const codPendingSum = list.filter(o => (o.payment_method || '').toLowerCase() === 'cod' && o.cod_status !== 'collected').reduce((s, o) => s + (o.total_amount || 0), 0);
    const footerExtra = showCodBreakdown
      ? `<tr><td colspan="7" class="right">COD Collected</td><td class="right">\u20B9${codCollectedSum.toFixed(2)}</td></tr>
         <tr><td colspan="7" class="right">COD Pending</td><td class="right">\u20B9${codPendingSum.toFixed(2)}</td></tr>`
      : '';
    const signatureHtml = signatureBlock
      ? `<div class="sig"><div class="sig-box"><div class="sig-line"></div><div class="sig-label">Handed over by (Admin)</div></div><div class="sig-box"><div class="sig-line"></div><div class="sig-label">Received by (Accounts)</div></div></div>`
      : '';
    w.document.write(`<!doctype html><html><head><title>${title} — ${siteName}</title>
      <style>
        body { font-family: Manrope, Arial, sans-serif; margin: 24px; color: #1A237E; }
        h1 { font-family: 'Playfair Display', serif; margin: 0 0 4px 0; font-size: 22px; }
        .sub { font-size: 11px; color: #666; margin-bottom: 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 11px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: top; }
        th { background: #f5f5f5; text-transform: uppercase; letter-spacing: .5px; font-size: 10px; }
        .right { text-align: right; }
        .muted { color: #888; font-size: 10px; }
        tfoot td { font-weight: bold; background: #fafafa; }
        .sig { display: flex; justify-content: space-between; gap: 40px; margin-top: 56px; }
        .sig-box { flex: 1; text-align: center; }
        .sig-line { border-top: 1px solid #555; margin-bottom: 6px; height: 1px; }
        .sig-label { font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: .5px; }
        @media print { body { margin: 12mm; } button { display: none; } }
      </style></head><body>
      <h1>${siteName} — ${title}</h1>
      <div class="sub">Generated ${new Date().toLocaleString('en-IN')} · ${list.length} order${list.length === 1 ? '' : 's'}${subtitle ? ' · ' + subtitle : ''}</div>
      <table>
        <thead><tr><th>Order</th><th>Date</th><th>Customer</th><th>Address</th><th>Items</th><th>Payment</th><th>Status</th><th class="right">Total</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="7" class="right">Grand Total</td><td class="right">\u20B9${totalSum.toFixed(2)}</td></tr>${footerExtra}</tfoot>
      </table>
      ${signatureHtml}
      <script>setTimeout(() => { window.print(); }, 250);</script>
      </body></html>`);
    w.document.close();
  };

  const printOrders = (codOnly = false) => {
    const list = codOnly ? filteredOrders.filter(o => (o.payment_method || '').toLowerCase() === 'cod') : filteredOrders;
    const subtitle = (dateFrom || dateTo) ? `${dateFrom || '…'} → ${dateTo || '…'}` : '';
    printOrdersList(list, { title: codOnly ? 'COD Orders' : 'Orders', subtitle, showCodBreakdown: codOnly });
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const todayCodCollected = orders.filter(o => (o.payment_method || '').toLowerCase() === 'cod' && o.cod_status === 'collected' && o.created_at && new Date(o.created_at).toISOString().slice(0, 10) === todayIso);
  const todayCodPending = orders.filter(o => (o.payment_method || '').toLowerCase() === 'cod' && o.cod_status !== 'collected' && o.created_at && new Date(o.created_at).toISOString().slice(0, 10) === todayIso);
  const todayCollectedSum = todayCodCollected.reduce((s, o) => s + (o.total_amount || 0), 0);
  const todayPendingSum = todayCodPending.reduce((s, o) => s + (o.total_amount || 0), 0);

  const printReconciliation = () => {
    if (!todayCodCollected.length) { toast.error('No COD collected today yet'); return; }
    printOrdersList(todayCodCollected, {
      title: 'Daily COD Reconciliation',
      subtitle: `${todayIso} · Collected today`,
      showCodBreakdown: true,
      signatureBlock: true,
    });
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-orders-heading">All Orders</h1>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => printOrders(false)} data-testid="print-all-orders-btn">
              <Printer className="h-4 w-4 mr-1" /> Print All
            </Button>
            <Button size="sm" variant="outline" onClick={() => printOrders(true)} data-testid="print-cod-orders-btn">
              <Printer className="h-4 w-4 mr-1" /> Print COD Only
            </Button>
            <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Daily Cash Summary Chip */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-primary/10 via-white to-accent/10 border border-[#E0E0E0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="daily-cash-summary">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
              <BanknoteArrowUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Today · {todayIso}</p>
              <p className="text-sm">
                <span className="text-primary font-bold text-base" data-testid="today-cod-collected">{'\u20B9'}{todayCollectedSum.toFixed(2)}</span>
                <span className="text-muted-foreground"> collected</span>
                <span className="mx-2 text-muted-foreground">·</span>
                <span className="text-amber-700 font-bold text-base" data-testid="today-cod-pending">{'\u20B9'}{todayPendingSum.toFixed(2)}</span>
                <span className="text-muted-foreground"> pending</span>
                <span className="ml-3 text-xs text-muted-foreground">({todayCodCollected.length} collected, {todayCodPending.length} pending)</span>
              </p>
            </div>
          </div>
          <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" onClick={printReconciliation} disabled={!todayCodCollected.length} data-testid="print-reconciliation-btn">
            <Printer className="h-4 w-4 mr-1" /> Print Reconciliation Sheet
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6 items-start sm:items-center">
          <Input placeholder="Search by order ID or customer name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-sm" data-testid="orders-search-input" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm w-40" data-testid="orders-status-filter">
            <option value="all">All Statuses</option>
            {['pending', 'paid', 'shipped', 'delivered', 'cancelled', 'refunded', 'rto'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={codStatusFilter} onChange={(e) => setCodStatusFilter(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm w-44" data-testid="orders-cod-filter">
            <option value="all">All Payments</option>
            <option value="cod_pending">COD · Pending Collection</option>
            <option value="cod_collected">COD · Collected</option>
            <option value="non_cod">Non-COD (Razorpay)</option>
          </select>
          <div className="flex items-center gap-2" data-testid="date-range-filter">
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="date-from-input" />
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="date-to-input" />
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={setToday} data-testid="date-today-btn">Today</Button>
            {(dateFrom || dateTo) && (
              <Button size="sm" variant="ghost" className="h-9 text-xs text-muted-foreground" onClick={clearDates} data-testid="date-clear-btn">Clear</Button>
            )}
          </div>
          <span className="text-xs text-muted-foreground self-center" data-testid="filter-count">Showing {filteredOrders.length} of {orders.length}</span>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-20"><p className="text-muted-foreground" data-testid="no-orders-admin-message">No orders yet</p></div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div key={order.order_id} className="p-5 rounded-xl bg-white border border-[#E0E0E0]" data-testid={`admin-order-${order.order_id}`}>
                <div className="flex flex-col md:flex-row justify-between items-start gap-3 mb-3">
                  <div>
                    <h3 className="font-bold font-heading text-sm">Order #{order.order_id}</h3>
                    <p className="text-xs text-muted-foreground">User: {order.user_id}</p>
                    <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                    {order.tracking_id && <p className="text-xs text-primary mt-1 flex items-center gap-1"><Truck className="h-3 w-3" /> {order.tracking_id}</p>}
                    {order.awb_code && (
                      <p className="text-xs text-muted-foreground mt-1">AWB: <a href={`https://shiprocket.co/tracking/${order.awb_code}`} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid={`track-link-${order.order_id}`}>{order.awb_code}</a></p>
                    )}
                    {order.courier_name && <p className="text-xs text-muted-foreground">Courier: {order.courier_name}</p>}
                    {!order.tracking_id && order.courier_override_name && (
                      <p className="text-xs text-accent font-medium" data-testid={`override-badge-${order.order_id}`}>
                        Override: {order.courier_override_name}
                      </p>
                    )}
                    {order.shipping_cost > 0 && <p className="text-xs text-muted-foreground">Shipping: {'\u20B9'}{order.shipping_cost}</p>}
                    {(order.payment_method || '').toLowerCase() === 'cod' && order.cod_status === 'collected' && order.cod_collected_at && (
                      <p className="text-xs text-primary mt-1" data-testid={`cod-collected-by-${order.order_id}`}>
                        Collected on {new Date(order.cod_collected_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        {order.cod_collected_by?.email ? ` by ${order.cod_collected_by.email}` : ''}
                      </p>
                    )}
                    {order.shipping_address && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {order.shipping_address.line1}, {order.shipping_address.city} — {order.shipping_address.pincode}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-primary">{'\u20B9'}{order.total_amount.toFixed(2)}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}`}>{order.status}</span>
                    {(order.payment_method || '').toLowerCase() === 'cod' && (
                      order.cod_status === 'collected' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary flex items-center gap-1" data-testid={`cod-badge-collected-${order.order_id}`}>
                          <CheckCircle2 className="h-3 w-3" /> COD Collected
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid={`cod-badge-pending-${order.order_id}`}>
                          COD Pending
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div className="text-sm space-y-1 mb-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between"><span>{item.name} x{item.quantity}</span><span>{'\u20B9'}{(item.quantity * item.price).toFixed(2)}</span></div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E0E0E0]">
                  {/* When an order is cancelled, only the invoice download + email
                      remain enabled — no further status changes / shipments / COD
                      ops / refunds make sense on a cancelled order. */}
                  {order.status === 'cancelled' && (
                    <span className="inline-flex items-center text-[11px] uppercase font-bold tracking-wide bg-gray-100 text-gray-700 rounded-full px-2 py-0.5" data-testid={`cancelled-tag-${order.order_id}`}>
                      <Ban className="h-3 w-3 mr-1" /> Cancelled — read-only
                    </span>
                  )}
                  {/* Status buttons */}
                  <div className="flex gap-1">
                    {STATUSES.map((s) => (
                      <Button key={s} size="sm" variant={order.status === s ? 'default' : 'outline'} className="rounded-full text-[10px] h-6 px-2" onClick={() => updateStatus(order.order_id, s)} disabled={order.status === s || order.status === 'cancelled'} data-testid={`status-${s}-${order.order_id}`}>
                        {s}
                      </Button>
                    ))}
                  </div>

                  {!order.tracking_id && order.status !== 'cancelled' && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => openShippingDialog(order)} data-testid={`ship-order-${order.order_id}`}>
                      <Truck className="h-3 w-3 mr-1" /> Ship
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => downloadInvoice(order.order_id)} data-testid={`download-invoice-admin-${order.order_id}`}>
                    <FileDown className="h-3 w-3 mr-1" /> Invoice
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => emailInvoice(order.order_id)} data-testid={`email-invoice-${order.order_id}`}>
                    <Mail className="h-3 w-3 mr-1" /> Email Invoice
                  </Button>
                  {order.status !== 'cancelled' && (order.payment_method || '').toLowerCase() === 'cod' && order.cod_status !== 'collected' && (
                    <Button size="sm" className="rounded-full text-xs bg-primary hover:bg-primary/90" onClick={() => collectCOD(order.order_id)} data-testid={`cod-collect-${order.order_id}`}>
                      <BanknoteArrowUp className="h-3 w-3 mr-1" /> Mark COD Collected
                    </Button>
                  )}
                  {order.status !== 'cancelled' && (order.payment_method || '').toLowerCase() === 'cod' && order.cod_status === 'collected' && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-muted-foreground" onClick={() => undoCollectCOD(order.order_id)} data-testid={`cod-undo-${order.order_id}`}>
                      <Undo2 className="h-3 w-3 mr-1" /> Undo Collect
                    </Button>
                  )}
                  {(order.payment_method || '').toLowerCase() === 'cod' && (order.status === 'pending' || order.status === 'paid') && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => cancelOrder(order.order_id)} data-testid={`cancel-order-${order.order_id}`}>
                      <Ban className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                  {(order.status === 'paid' || order.status === 'shipped' || order.status === 'delivered') && order.status !== 'refunded' && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => refundOrder(order.order_id, order.total_amount)} data-testid={`refund-order-${order.order_id}`}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Refund
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => deleteOrder(order.order_id)} data-testid={`delete-order-${order.order_id}`} title="Permanently delete this order (admin)">
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={!!shippingDialog} onOpenChange={() => setShippingDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Shipment</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Order: {shippingDialog}</p>

              {resolvedCourier && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs" data-testid="resolved-courier-preview">
                  <p className="font-semibold text-primary mb-0.5">Auto-resolved courier</p>
                  <p className="text-muted-foreground">
                    {resolvedCourier.courier_name || `Courier #${resolvedCourier.courier_id}`} —
                    <span className="ml-1 capitalize">{resolvedCourier.source}</span>
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Delivery Pincode (optional — auto-filled from address)</label>
                <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Leave empty to use order address" data-testid="shipping-postcode-input" />
              </div>

              <div data-testid="pickup-location-section">
                <label className="text-sm font-medium">Pickup Location *</label>
                <select
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="pickup-location-select"
                >
                  {pickupLocations.length === 0 && <option value="">{pickupError ? '— Failed to load —' : '— Loading pickup locations… —'}</option>}
                  {pickupLocations.map((loc) => (
                    <option key={loc.pickup_location} value={loc.pickup_location} data-testid={`pickup-location-option-${loc.pickup_location}`}>
                      {loc.pickup_location}{loc.is_primary ? ' (default)' : ''}{loc.city ? ` — ${loc.city}` : ''}{loc.pin_code ? `, ${loc.pin_code}` : ''}
                    </option>
                  ))}
                </select>
                {pickupError && <p className="text-[11px] text-destructive mt-1" data-testid="pickup-location-error">{pickupError}</p>}
                <p className="text-[11px] text-muted-foreground mt-1">Pulled live from Shiprocket · used for both forward and return shipments.</p>
              </div>

              <div className="rounded-lg border border-[#E0E0E0] p-3 bg-[#FAFAFA]">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Packaging Details — Recipient Name *</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">First Name *</label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Enter first name" data-testid="shipping-first-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Last Name *</label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Enter last name" data-testid="shipping-last-name-input" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Legacy orders may not have these split — please enter them before Ship. Never auto-split from a combined name.</p>
              </div>

              <div className="rounded-lg border border-[#E0E0E0] p-3 bg-[#FAFAFA]" data-testid="packaging-details-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Packaging Details — Parcel Dimensions *</p>
                  {pkgSaved && <span className="text-[10px] text-primary font-semibold uppercase" data-testid="packaging-saved-badge">Saved</span>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">Length (cm) *</label>
                    <Input type="number" min="0" step="0.01" value={pkg.length} onChange={(e) => { setPkg({ ...pkg, length: e.target.value }); setPkgSaved(false); }} placeholder="e.g., 20" data-testid="pkg-length-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Breadth (cm) *</label>
                    <Input type="number" min="0" step="0.01" value={pkg.breadth} onChange={(e) => { setPkg({ ...pkg, breadth: e.target.value }); setPkgSaved(false); }} placeholder="e.g., 15" data-testid="pkg-breadth-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Height (cm) *</label>
                    <Input type="number" min="0" step="0.01" value={pkg.height} onChange={(e) => { setPkg({ ...pkg, height: e.target.value }); setPkgSaved(false); }} placeholder="e.g., 10" data-testid="pkg-height-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Gross Weight (kg) *</label>
                    <Input type="number" min="0" step="0.01" value={pkg.weight} onChange={(e) => { setPkg({ ...pkg, weight: e.target.value }); setPkgSaved(false); }} placeholder="e.g., 0.5" data-testid="pkg-weight-input" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">One set of dimensions per order — describes the whole parcel, not individual items. All four must be greater than zero.</p>
                <Button size="sm" variant="outline" className="mt-2 rounded-full text-xs" onClick={savePackaging} disabled={savingPkg} data-testid="save-packaging-btn">
                  {savingPkg ? 'Saving…' : (pkgSaved ? 'Update Packaging' : 'Save Packaging')}
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium">Courier override (optional — overrides rule for this order)</label>
                <div className="flex gap-2 mt-1">
                  <select
                    value={overrideCourierId}
                    onChange={(e) => setOverrideCourierId(e.target.value)}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="override-courier-select"
                  >
                    <option value="">Use rule / default</option>
                    {couriers.map((c) => (
                      <option key={c.courier_company_id} value={c.courier_company_id}>
                        {c.courier_name} (id {c.courier_company_id})
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="outline" onClick={saveCourierOverride} data-testid="save-override-btn">Save</Button>
                </div>
              </div>

              <Button className="w-full" onClick={() => createShipment(shippingDialog)} disabled={!pkgSaved || !pickupLocation} data-testid="confirm-shipment-button">
                <Package className="h-4 w-4 mr-2" /> {(!pkgSaved) ? 'Save Packaging to Enable Ship' : (!pickupLocation ? 'Select Pickup Location' : 'Confirm Shipment')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminOrders;
